import React, { useCallback, useRef } from 'react';
import { useSingleKeyShortcut } from '@hooks/useSingleKeyShortcut';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Box, Button, Stack, Typography } from '@mui/material';

import { AppliedFilterChips } from './AppliedFilterChips';
import { FilterMenuPopover } from './FilterMenuPopover';
import { FilterToolbarButton } from './FilterToolbarButton';
import type { FilterFieldDef, FiltersState } from './types';
import { useFilterMenuState } from './useFilterMenuState';

export type FilterBarProps = {
  canClearPredicates: boolean;
  canResetView: boolean;
  fields: FilterFieldDef[];
  predicates: FiltersState;
  resultStatement: string;
  utilityControl?: React.ReactNode;
  onPredicatesChange: (predicates: FiltersState) => void;
  onClearPredicates: () => void;
  onResetView: () => void;
};

/** Shared Linear-style filter trigger, applied chips, and cascading editor. */
export const FilterBar = ({
  canClearPredicates,
  canResetView,
  fields,
  predicates,
  resultStatement,
  utilityControl,
  onPredicatesChange,
  onClearPredicates,
  onResetView,
}: FilterBarProps) => {
  const { isOpen, openEditor, openMenu, popoverProps, toggleMenu } = useFilterMenuState();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const appliedCount = Object.keys(predicates).length;

  const toggleFromTrigger = useCallback(() => {
    if (triggerRef.current) toggleMenu(triggerRef.current);
  }, [toggleMenu]);

  const openFromShortcut = useCallback(() => {
    if (triggerRef.current) openMenu(triggerRef.current);
  }, [openMenu]);

  useSingleKeyShortcut('f', openFromShortcut, { enabled: !isOpen });

  const clearPredicates = useCallback(() => {
    onClearPredicates();
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  }, [onClearPredicates]);

  return (
    <Stack spacing={appliedCount > 0 || canResetView ? 1.25 : 0}>
      <Box
        sx={{
          alignItems: { xs: 'stretch', sm: 'center' },
          display: 'grid',
          gap: 1,
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 1fr) auto' },
        }}
      >
        <Typography
          aria-atomic="true"
          aria-live="polite"
          color="text.secondary"
          role="status"
          variant="caption"
        >
          {resultStatement}
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
          }}
        >
          <FilterToolbarButton
            ref={triggerRef}
            aria-expanded={isOpen}
            aria-haspopup="dialog"
            aria-label={`${isOpen ? 'Close' : 'Open'} filter menu${
              appliedCount > 0 ? ` (${appliedCount} active)` : ''
            }`}
            onClick={toggleFromTrigger}
            startIcon={<FilterListIcon />}
          >
            Filters{appliedCount > 0 ? ` (${appliedCount})` : ''}
          </FilterToolbarButton>
          {utilityControl}
        </Stack>
      </Box>

      {(appliedCount > 0 || canResetView) && (
        <Box
          sx={{
            alignItems: { xs: 'stretch', md: 'center' },
            display: 'grid',
            gap: 1,
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) auto' },
          }}
        >
          {appliedCount > 0 ? (
            <Stack
              aria-label="Active filters"
              direction="row"
              role="group"
              sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 0.5, minHeight: 32 }}
            >
              <AppliedFilterChips
                fields={fields}
                value={predicates}
                onEdit={openEditor}
                onRemove={fieldId =>
                  onPredicatesChange(
                    Object.fromEntries(
                      Object.entries(predicates).filter(([candidate]) => candidate !== fieldId),
                    ),
                  )
                }
              />
              <Button
                onClick={event => openMenu(event.currentTarget)}
                size="small"
                startIcon={<AddIcon />}
                sx={{ textTransform: 'none' }}
                variant="text"
              >
                Add filter
              </Button>
              {canClearPredicates && (
                <Button
                  aria-label="Clear filters"
                  onClick={clearPredicates}
                  size="small"
                  sx={{ textTransform: 'none' }}
                  variant="text"
                >
                  Clear filters
                </Button>
              )}
            </Stack>
          ) : (
            <Box />
          )}
          {canResetView && (
            <Button
              onClick={onResetView}
              size="small"
              startIcon={<RestartAltIcon />}
              sx={{ justifySelf: { md: 'end' }, textTransform: 'none' }}
              variant="text"
            >
              Reset to default
            </Button>
          )}
        </Box>
      )}

      <FilterMenuPopover
        {...popoverProps}
        fields={fields}
        value={predicates}
        onChange={onPredicatesChange}
      />
    </Stack>
  );
};
